import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Plus, Trash2, Save, Loader2, Code2, Server, Database, Layout, Terminal, Cpu, Edit2, X, Star, Layers } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ImageUpload from './ImageUpload';

interface SkillItem {
    id: string;
    name: string;
    icon: string;
    logoUrl?: string;
    proficiency: number;
    category: 'frontend' | 'backend' | 'tools' | 'design';
    yearsOfExperience?: number;
}

export default function SkillsManager() {
    const [skills, setSkills] = useState<SkillItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);

    const [newSkill, setNewSkill] = useState<Partial<SkillItem>>({
        name: '',
        icon: 'Code2',
        logoUrl: '',
        proficiency: 3,
        category: 'frontend',
        yearsOfExperience: 1
    });

    const [editSkill, setEditSkill] = useState<Partial<SkillItem>>({});

    useEffect(() => {
        fetchSkills();
    }, []);

    const fetchSkills = async () => {
        try {
            const { data, error } = await supabase
                .from('site_content')
                .select('content')
                .eq('section_key', 'skills')
                .maybeSingle();

            if (error) throw error;
            if (data && data.content) {
                setSkills(data.content);
            }
        } catch (err) {
            console.error('Error fetching skills:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (updatedSkills: SkillItem[]) => {
        setSaving(true);
        try {
            const { error } = await supabase
                .from('site_content')
                .upsert({
                    section_key: 'skills',
                    content: updatedSkills,
                    updated_at: new Date().toISOString()
                });

            if (error) throw error;
            setSkills(updatedSkills);
            setEditingId(null);
        } catch (err) {
            console.error('Error saving skills:', err);
            alert('Failed to save skills');
        } finally {
            setSaving(false);
        }
    };

    const addSkill = () => {
        if (!newSkill.name) return;

        const skillToAdd: SkillItem = {
            id: Math.random().toString(36).substr(2, 9),
            name: newSkill.name,
            icon: newSkill.icon || 'Code2',
            logoUrl: newSkill.logoUrl,
            proficiency: newSkill.proficiency || 3,
            category: newSkill.category || 'frontend',
            yearsOfExperience: newSkill.yearsOfExperience || 1
        };

        const updated = [...skills, skillToAdd];
        handleSave(updated);
        setNewSkill({ name: '', icon: 'Code2', logoUrl: '', proficiency: 3, category: 'frontend', yearsOfExperience: 1 });
    };

    const startEdit = (skill: SkillItem) => {
        setEditingId(skill.id);
        setEditSkill(skill);
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditSkill({});
    };

    const saveEdit = () => {
        if (!editingId) return;
        const updated = skills.map(s => s.id === editingId ? { ...s, ...editSkill } : s);
        handleSave(updated);
    };

    const deleteSkill = (id: string) => {
        if (!confirm('Delete this skill?')) return;
        const updated = skills.filter(s => s.id !== id);
        handleSave(updated);
    };

    const getIconComponent = (iconName: string) => {
        const icons: any = { Code2, Server, Database, Layout, Terminal, Cpu };
        const IconComponent = icons[iconName] || Code2;
        return <IconComponent size={24} />;
    };

    if (loading) return <div className="p-12 flex justify-center"><Loader2 className="animate-spin text-blue-500 w-8 h-8" /></div>;

    return (
        <div className="space-y-10 max-w-6xl mx-auto py-10">
            {/* Add New Skill Form - Vibrant Style */}
            <div className="glass-vibrant border-2 border-cyan-500/20 rounded-[2.5rem] p-10 shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none" />

                <div className="flex items-center gap-5 mb-10">
                    <div className="p-4 bg-cyan-500/10 rounded-2xl text-cyan-400 border border-cyan-500/20 shadow-[0_0_15px_rgba(6,182,212,0.2)]">
                        <Plus size={28} />
                    </div>
                    <div>
                        <h2 className="text-3xl font-black text-white tracking-tighter ">Initialize <span className="text-cyan-400">Skill Module</span></h2>
                        <p className="text-[10px] font-black text-gray-500  tracking-widest">Protocol for technical repertoire expansion</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
                    <div className="space-y-3">
                        <label className="block text-[10px] font-black text-gray-400  tracking-widest">Skill Designation</label>
                        <input
                            type="text"
                            value={newSkill.name}
                            onChange={(e) => setNewSkill({ ...newSkill, name: e.target.value })}
                            className="w-full bg-white/5 border-2 border-white/5 rounded-2xl text-white px-5 py-4 focus:border-cyan-500/50 focus:outline-none transition-all placeholder:text-gray-700"
                            placeholder="e.g., React"
                        />
                    </div>
                    <div className="space-y-3">
                        <label className="block text-[10px] font-black text-gray-400  tracking-widest">Tier Category</label>
                        <select
                            value={newSkill.category}
                            onChange={(e) => setNewSkill({ ...newSkill, category: e.target.value as any })}
                            className="w-full bg-white/5 border-2 border-white/5 rounded-2xl text-white px-5 py-4 focus:border-cyan-500/50 focus:outline-none cursor-pointer"
                        >
                            <option value="frontend">Frontend Architecture</option>
                            <option value="backend">Backend Systems</option>
                            <option value="tools">DevOps & Tools</option>
                            <option value="design">UI/UX Design</option>
                        </select>
                    </div>
                    <div className="space-y-3">
                        <label className="block text-[10px] font-black text-gray-400  tracking-widest">Mastery Level</label>
                        <div className="flex items-center gap-3 pt-2">
                            {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                    key={star}
                                    onClick={() => setNewSkill({ ...newSkill, proficiency: star })}
                                    className={`p-1 transition-all duration-300 ${newSkill.proficiency! >= star ? 'text-cyan-400 scale-110' : 'text-gray-700 hover:text-gray-500'}`}
                                >
                                    <Star size={28} fill={newSkill.proficiency! >= star ? 'currentColor' : 'none'} className={newSkill.proficiency! >= star ? 'drop-shadow-[0_0_8px_rgba(6,182,212,0.8)]' : ''} />
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="space-y-3">
                        <label className="block text-[10px] font-black text-gray-400  tracking-widest">Service Duration (y)</label>
                        <input
                            type="number"
                            min="0"
                            value={newSkill.yearsOfExperience}
                            onChange={(e) => setNewSkill({ ...newSkill, yearsOfExperience: parseInt(e.target.value) })}
                            className="w-full bg-white/5 border-2 border-white/5 rounded-2xl text-white px-5 py-4 focus:border-cyan-500/50 focus:outline-none"
                        />
                    </div>
                </div>

                <div className="mb-10 border-t border-white/5 pt-10">
                    <label className="text-[10px] font-black text-gray-400  tracking-[0.2em] mb-6 flex items-center gap-3">
                        <Layers size={18} className="text-cyan-400" /> Digital Visual Identity (Logo)
                    </label>
                    <div className="max-w-md">
                        <ImageUpload
                            value={newSkill.logoUrl || null}
                            onChange={(url) => setNewSkill({ ...newSkill, logoUrl: url })}
                            folder="skills"
                        />
                    </div>
                </div>

                <div className="flex justify-end pt-8 border-t border-white/5">
                    <button
                        onClick={addSkill}
                        disabled={saving || !newSkill.name}
                        className="btn-vibrant-cyan flex items-center gap-3"
                    >
                        {saving ? <Loader2 className="animate-spin h-5 w-5" /> : <Plus size={20} />}
                        Sync Skill to Profile
                    </button>
                </div>
            </div>

            {/* Skills List with Neon Vibes */}
            <div className="space-y-8">
                <div className="flex justify-between items-end px-10">
                    <div>
                        <h2 className="text-3xl font-black text-white tracking-tighter ">Active <span className="text-fuchsia-500">Inventory</span></h2>
                        <p className="text-[10px] font-black text-gray-500  tracking-[0.3em] mt-1">Status: {skills.length} Modules Synced</p>
                    </div>
                    <div className="flex gap-3">
                        {['frontend', 'backend', 'tools', 'design'].map(cat => (
                            <span key={cat} className="text-[8px] font-black  tracking-widest text-gray-600 bg-white/2 px-3 py-1.5 rounded-full border border-white/5">
                                {cat}
                            </span>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    <AnimatePresence mode="popLayout">
                        {skills.map((skill, index) => (
                            <motion.div
                                key={skill.id}
                                layout
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                transition={{ delay: index * 0.05 }}
                            >
                                {editingId === skill.id ? (
                                    <div className="glass-vibrant border-2 border-cyan-500 rounded-[2.5rem] p-8 space-y-6 shadow-[0_0_50px_rgba(6,182,212,0.2)] scale-105 z-20 relative backdrop-blur-3xl">
                                        <div className="space-y-5">
                                            <input
                                                type="text"
                                                value={editSkill.name}
                                                onChange={(e) => setEditSkill({ ...editSkill, name: e.target.value })}
                                                className="w-full bg-white/5 border-2 border-white/5 rounded-2xl text-white px-5 py-3 text-sm focus:border-cyan-500/50"
                                                placeholder="Skill name"
                                            />
                                            <div className="grid grid-cols-2 gap-4">
                                                <select
                                                    value={editSkill.category}
                                                    onChange={(e) => setEditSkill({ ...editSkill, category: e.target.value as any })}
                                                    className="w-full bg-white/5 border-2 border-white/5 rounded-2xl text-white px-5 py-3 text-sm"
                                                >
                                                    <option value="frontend">Frontend</option>
                                                    <option value="backend">Backend</option>
                                                    <option value="tools">Tools</option>
                                                    <option value="design">Design</option>
                                                </select>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="5"
                                                    value={editSkill.proficiency}
                                                    onChange={(e) => setEditSkill({ ...editSkill, proficiency: parseInt(e.target.value) })}
                                                    className="w-full bg-white/5 border-2 border-white/5 rounded-2xl text-white px-5 py-3 text-sm"
                                                />
                                            </div>
                                            <ImageUpload
                                                value={editSkill.logoUrl || null}
                                                onChange={(url) => setEditSkill({ ...editSkill, logoUrl: url })}
                                                folder="skills"
                                            />
                                        </div>
                                        <div className="flex gap-4 pt-4">
                                            <button
                                                onClick={saveEdit}
                                                className="flex-1 btn-vibrant-cyan py-3"
                                            >
                                                Commit
                                            </button>
                                            <button
                                                onClick={cancelEdit}
                                                className="flex-1 btn-vibrant-outline py-3"
                                            >
                                                Abort
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="glass border-2 border-white/5 rounded-[2.5rem] p-8 flex items-center gap-6 group hover:border-cyan-500/40 transition-all duration-500 shadow-2xl relative overflow-hidden min-h-[140px]">
                                        <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br transition-opacity opacity-5 pointer-events-none group-hover:opacity-10 ${skill.category === 'frontend' ? 'from-cyan-400' :
                                                skill.category === 'backend' ? 'from-fuchsia-500' :
                                                    skill.category === 'design' ? 'from-purple-500' : 'from-orange-500'
                                            }`} />

                                        <div className="flex-shrink-0 relative">
                                            {skill.logoUrl ? (
                                                <img src={skill.logoUrl} alt={skill.name} className="h-20 w-20 object-contain rounded-3xl bg-white p-3 shadow-2xl group-hover:scale-110 transition-transform duration-500 border border-white/10" />
                                            ) : (
                                                <div className="h-20 w-20 rounded-3xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400 group-hover:scale-110 group-hover:shadow-[0_0_20px_rgba(6,182,212,0.3)] transition-all duration-500">
                                                    {getIconComponent(skill.icon)}
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex-grow">
                                            <div className="flex items-center justify-between mb-4">
                                                <h3 className="font-black text-white text-xl group-hover:text-cyan-400 transition-colors tracking-tight">{skill.name}</h3>
                                                <div className="flex gap-2 transition-all">
                                                    <button
                                                        onClick={() => startEdit(skill)}
                                                        className="text-gray-400 hover:text-cyan-400 p-2.5 rounded-xl bg-white/5 border border-white/10 hover:border-cyan-500/30 transition-all shadow-sm"
                                                        title="Edit Skill"
                                                    >
                                                        <Edit2 size={18} />
                                                    </button>
                                                    <button
                                                        onClick={() => deleteSkill(skill.id)}
                                                        className="text-gray-400 hover:text-red-500 p-2.5 rounded-xl bg-white/5 border border-white/10 hover:border-red-500/30 transition-all shadow-sm"
                                                        title="Delete Skill"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="flex flex-col gap-3">
                                                <div className="flex items-center gap-1.5 h-1.5 w-full bg-white/5 rounded-full overflow-hidden border border-white/5">
                                                    <motion.div
                                                        initial={{ width: 0 }}
                                                        whileInView={{ width: `${(skill.proficiency / 5) * 100}%` }}
                                                        className="h-full bg-gradient-to-r from-cyan-600 to-cyan-400 shadow-[0_0_10px_rgba(0,210,255,0.6)]"
                                                    />
                                                </div>
                                                <div className="flex items-center justify-between text-[8px] font-black text-gray-600  tracking-widest">
                                                    <span className="text-cyan-400/60">{skill.category}</span>
                                                    {skill.yearsOfExperience ? <span>Exp Phase: {skill.yearsOfExperience}y</span> : null}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </AnimatePresence>
                </div>

                {skills.length === 0 && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-center py-24 glass-vibrant border-dashed border-cyan-500/20 rounded-[3rem]"
                    >
                        <div className="p-6 bg-cyan-500/5 w-fit mx-auto rounded-full mb-6 border border-cyan-500/10">
                            <Cpu size={64} className="text-cyan-400/20" />
                        </div>
                        <p className="text-2xl font-black text-white  tracking-tighter">Skill Vault Empty</p>
                        <p className="text-xs font-bold text-gray-600  tracking-[0.2em] mt-2">Initialize core technical modules above</p>
                    </motion.div>
                )}
            </div>
        </div>
    );
}
